import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useGetDocumentsQuery, useLazyGetCurrentUserQuery } from 'api/main';
import { useSendIdVerificationMutation, useStartIdVerificationMutation } from 'api/tunnelSteps';
import {
    getDocumentAvailability,
    getDocumentNameByType,
    getUserIdFromIri,
    setTunnelChangesHandlers,
} from 'utils/helpers';
import { userSelectors } from 'reducers';
import { texts } from 'utils/languages/fr';
import { docTypes, stepIDs, stepStatus } from 'utils/constants';

import TunnelStep from '../common/TunnelStep';
import { Iframe, SubmitButton, InfoButton, DocumentLink, Loader } from 'components';
import { Typography } from '@mui/material';

const IDCheck = props => {
    const { userStep, handleNextStep } = props;

    const [getCurrentUser, { isFetching: userLoading }] = useLazyGetCurrentUserQuery();
    const [startIdVerification, { isLoading: startLoader, data: jumioSession, isUninitialized }] =
        useStartIdVerificationMutation();
    const [sendIdVerification, { isLoading: sendLoader }] = useSendIdVerificationMutation();

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const loggingOut = useSelector(userSelectors.getLogoutLoader);

    const [frameError, setFrameError] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [frameLoaded, setFrameLoaded] = useState(false);
    const alerts = useMemo(() => userStep?.alerts || [], [userStep]);
    const stepIncomplete = useMemo(() => userStep?.status === stepStatus.INCOMPLETE, [userStep]);
    const stepCompleted = useMemo(() => userStep?.status === stepStatus.COMPLETED, [userStep]);
    const stepFailed = useMemo(() => userStep?.status === stepStatus.FAILED, [userStep]);
    const stepVerified = useMemo(() => userStep?.status === stepStatus.VERIFIED, [userStep]);

    const isDocumentAvailable = useMemo(() => {
        return getDocumentAvailability(tunnelSubscription?.userTunnelSteps, stepIDs.ID_CHECK);
    }, [tunnelSubscription]);

    const { data: documents = [], isLoading: documentsLoader } = useGetDocumentsQuery(
        {
            belongsTo: getUserIdFromIri(tunnelSubscription?.user),
            type: docTypes.ID,
        },
        { skip: !isDocumentAvailable }
    );

    const handleMessage = useCallback(
        e => {
            if (!e.data || typeof e.data !== 'string' || !e.origin.includes('jumio')) return;
            try {
                const data = window.JSON.parse(e.data);
                const value = data.payload?.value;

                if (value === 'loaded' && !frameLoaded) {
                    setFrameLoaded(true);
                }

                if (value === 'success' && !(stepCompleted || stepVerified)) {
                    sendIdVerification({
                        callback: () => handleNextStep(true),
                        stepId: stepIDs.ID_CHECK,
                    });
                }
                if (value === 'error' || value === 'redirectError') {
                    if (data.payload.metainfo.code === 9210) {
                        // session expired: refresh current user
                        setTimeout(() => {
                            getCurrentUser();
                        }, 1000);
                        setSessionExpired(true);
                    } else {
                        setFrameError(true);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        },
        [
            sendIdVerification,
            stepCompleted,
            stepVerified,
            frameLoaded,
            handleNextStep,
            getCurrentUser,
        ]
    );

    const handleResetFrame = useCallback(() => {
        startIdVerification();
        setFrameError(false);
        setRetrying(true);
    }, [startIdVerification]);

    useEffect(() => {
        getCurrentUser();
    }, [getCurrentUser]);

    useEffect(
        () =>
            setTunnelChangesHandlers(
                afterSubmitCallback => () => afterSubmitCallback(),
                () => true,
                frameLoaded
            ),
        [frameLoaded]
    );

    useEffect(() => {
        if (isUninitialized && !loggingOut && (!userStep || stepIncomplete)) {
            startIdVerification();
        }
    }, [userStep, stepIncomplete, startLoader, loggingOut, startIdVerification, isUninitialized]);

    useEffect(() => {
        if (stepCompleted || stepVerified) {
            return;
        }
        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage, stepCompleted, stepVerified]);

    const filteredAlerts = useMemo(() => alerts.filter(a => !a.solved), [alerts]);
    const showIframe = useMemo(
        () =>
            (jumioSession &&
                !frameError &&
                !stepCompleted &&
                !retrying &&
                (!userStep || stepIncomplete)) ||
            (jumioSession && retrying && !frameError && !!userStep),
        [frameError, jumioSession, stepCompleted, retrying, userStep, stepIncomplete]
    );

    return (
        <TunnelStep
            maxWidth={stepCompleted || stepVerified ? undefined : 1010}
            title={
                tunnelSubscription.withCoSubscriber
                    ? texts.PRIMARY_SUBSCRIBER_ID_CHECK
                    : texts.ID_CHECK
            }
            subtitle={
                <Typography variant="body1">
                    <i>{texts.DISCONNECT_REMINDER}</i>
                </Typography>
            }
        >
            {showIframe && (
                <Loader loading={userLoading || startLoader || sendLoader || documentsLoader}>
                    <InfoButton
                        disableMarginBottom
                        title={texts.ID_DOCUMENT_VERIFICATION_TITLE}
                        content={
                            <React.Fragment>
                                <Typography gutterBottom>
                                    {texts.ID_DOCUMENT_VERIFICATION_TEXT_1}
                                </Typography>
                                <Typography gutterBottom>
                                    {texts.ID_DOCUMENT_VERIFICATION_TEXT_2}
                                </Typography>
                            </React.Fragment>
                        }
                        sx={{ float: 'right' }}
                    />
                    <Iframe title={texts.ID_CHECK} src={jumioSession.redirectUrl} />
                </Loader>
            )}
            {stepCompleted && !frameError && (
                <Typography align="center">{texts.ATTEND_PROCESS}</Typography>
            )}
            {!startLoader &&
                !sendLoader &&
                !userLoading &&
                !documentsLoader &&
                !retrying &&
                (stepVerified || stepFailed) && (
                    <React.Fragment>
                        {frameError && <Typography align="center">{texts.FRAME_ERROR}</Typography>}
                        {!!filteredAlerts.length && (
                            <>
                                <Typography variant="body2" align="center">
                                    {texts.DOCUMENT_VERIFIE_AVEC_INCOHERENCES}
                                </Typography>
                                {documents[0] && (
                                    <DocumentLink
                                        isDocumentAvailable={isDocumentAvailable}
                                        document={documents[0]}
                                        label={getDocumentNameByType(
                                            documents[0]?.type,
                                            documents[0]?.subtype
                                        )}
                                    />
                                )}
                            </>
                        )}

                        {(frameError ||
                            sessionExpired ||
                            filteredAlerts.length !== 0 ||
                            stepFailed) &&
                            !stepVerified && (
                                <SubmitButton text={texts.RETRY} onClick={handleResetFrame} />
                            )}
                        {stepVerified && !filteredAlerts.length && (
                            <React.Fragment>
                                <Typography align="center">{texts.DOCUMENT_VERIFIE}</Typography>
                                {!!documents[0] && (
                                    <DocumentLink
                                        isDocumentAvailable={isDocumentAvailable}
                                        document={documents[0]}
                                        label={getDocumentNameByType(
                                            documents[0]?.type,
                                            documents[0]?.subtype
                                        )}
                                    />
                                )}
                            </React.Fragment>
                        )}
                    </React.Fragment>
                )}
        </TunnelStep>
    );
};

export default IDCheck;
